import clsx from "clsx";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  startOfMonth,
  subMonths,
} from "date-fns";
import { enUS } from "date-fns/locale/en-US";
import { pl } from "date-fns/locale/pl";
import {
  Button,
  LoadPanel,
  Lookup,
  Pagination,
  ScrollView,
} from "devextreme-react";
import _ from "lodash";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchWorkPlan, WorkPlan } from "../../api/calendarData";
import { useAuth } from "../../contexts/auth";
import { useCompanyContext } from "../../contexts/company";
import { ThemeContext } from "../../theme/theme";
import { useScreenSize } from "../../utils/media-query";
import "./Schedule.scss";

export const Schedule = () => {
  const { i18n } = useTranslation();
  const [lastLoadedMonth, setLastLoadedMonth] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [entries, setEntries] = useState<WorkPlan[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const { t } = useTranslation();
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(1);
  const { user } = useAuth();
  const { companyId } = useCompanyContext();
  const translatedMonth = t(`months.${format(currentMonth, "MMMM")}`);
  const themeContext = useContext(ThemeContext);
  const { isXSmall, isSmall } = useScreenSize();
  const isMobile = isXSmall || isSmall;
  const [schedule, setSchedule] = useState<
    Record<string, { hours: string; dayType: number | null }>
  >({});
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [viewMode, setViewMode] = useState("Change");
  const [selectedMobileEmployeeKey, setSelectedMobileEmployeeKey] =
    useState<string>("");

  useEffect(() => {
    const scrollSpeed = 1;
    const scrollInterval = 20;

    const interval = setInterval(() => {
      const el = scrollContainerRef.current;
      if (!el) return;

      const maxScroll = el.scrollHeight - el.clientHeight;

      if (el.scrollTop + el.clientHeight >= el.scrollHeight) {
        el.scrollTop = 0;
      } else {
        el.scrollTop += scrollSpeed;
      }
    }, scrollInterval);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchDataMonthly(currentMonth);
  }, [currentMonth, pageSize, pageIndex]);

  useEffect(() => {
    if (!isMobile || entries.length === 0) return;
    const exists = entries.some(
      (e) =>
        `${e.employee.companyId},${e.employee.id}` === selectedMobileEmployeeKey
    );
    if (!selectedMobileEmployeeKey || !exists) {
      const first = entries[0].employee;
      setSelectedMobileEmployeeKey(`${first.companyId},${first.id}`);
    }
  }, [entries, isMobile]);

  const fetchDataMonthly = (date: Date): Promise<void> => {
    setLastLoadedMonth(currentMonth);
    setIsLoading(true);

    return new Promise(async (resolve, reject) => {
      try {
        const formattedStartDate = format(
          startOfMonth(currentMonth),
          "yyyy-MM-dd"
        );
        const formattedEndDate = format(endOfMonth(currentMonth), "yyyy-MM-dd");
        const company = companyId === 0 ? user?.companyId || 0 : companyId;

        const result = await fetchWorkPlan(
          formattedStartDate,
          formattedEndDate,
          company,
          user?.employeeId || 0,
          pageIndex,
          pageSize
        );

        if (result.isOk && result.data) {
          setEntries(result.data.items);
          setTotalCount(result.data.totalCount);
        } else {
          setEntries([]);
          setTotalCount(0);
        }

        resolve();
      } catch (error) {
        reject(error);
      } finally {
        setIsLoading(false);
      }
    });
  };

  const changeMonth = (direction: "next" | "previous") => {
    const newMonth =
      direction === "next"
        ? addMonths(currentMonth, 1)
        : subMonths(currentMonth, 1);
    setCurrentMonth(newMonth);
  };

  const getClassForDay = (date, theme) => {
    const formattedDate = new Date(date).toLocaleDateString("en-CA");
    let dayType: number | null = null;

    const dayTypeFromEntries = entries
      .flatMap((entry) => entry.days)
      .find((day) => {
        const entryDate = new Date(day.date).toLocaleDateString("en-CA");
        return entryDate === formattedDate;
      })?.dayType;

    if (dayTypeFromEntries !== undefined) {
      schedule[formattedDate] = { dayType: dayTypeFromEntries, hours: "-" };
      dayType = dayTypeFromEntries;
    }

    return clsx({
      "dx-calendar-cell-work": dayType === 1,
      "dx-calendar-cell-free": dayType === 2,
      "dx-calendar-cell-holiday": dayType === 3,
      "dx-calendar-cell-udefind": dayType === null,
      "dx-calendar-cell-dark": theme === "dark",
    });
  };

  const getDayTypeFor = (date: Date | string): number | null => {
    const formattedDate = (
      date instanceof Date ? date : new Date(date)
    ).toLocaleDateString("en-CA");
    const dt = entries
      .flatMap((entry) => entry.days)
      .find(
        (day) =>
          new Date(day.date).toLocaleDateString("en-CA") === formattedDate
      )?.dayType;
    return dt ?? null;
  };

  const handlePageClick = (newPageIndex) => {
    setPageIndex(newPageIndex);
  };

  return (
    <ScrollView useNative>
      <div className="p-6">
        <div className="mb-6 text-2xl font-bold leading-none">
          {t("schedule.schedule")}
        </div>
        {isLoading ? (
          <LoadPanel shading shadingColor="rgba(0, 0, 0, 0.3)" visible />
        ) : entries.length === 0 ? (
          <div
            className="p-4 text-sm text-blue-800 rounded-lg bg-blue-100 dark:bg-gray-800 dark:text-blue-400"
            role="alert"
          >
            {t("noSubordinateEmployees")}
          </div>
        ) : (
          <>
            {isMobile ? (
              <>
                <div className="flex flex-col gap-2 mb-3">
                  <Lookup
                    dataSource={[...entries].sort((a, b) =>
                      a.employee.lastName.localeCompare(
                        b.employee.lastName,
                        "pl",
                        { sensitivity: "base" }
                      )
                    )}
                    value={selectedMobileEmployeeKey}
                    valueExpr={(item: WorkPlan | null) =>
                      item
                        ? `${item.employee.companyId},${item.employee.id}`
                        : ""
                    }
                    displayExpr={(item: WorkPlan | null) =>
                      item
                        ? `${item.employee.lastName} ${item.employee.firstName}`
                        : ""
                    }
                    width="100%"
                    onValueChanged={(e) =>
                      setSelectedMobileEmployeeKey(e.value)
                    }
                    labelMode="static"
                    label={t("schedule.employee")}
                    dropDownOptions={{ showTitle: false }}
                  />
                  <Button
                    text={`${t("hoursType")}`}
                    stylingMode="contained"
                    width="100%"
                    onClick={() =>
                      setViewMode((prev) =>
                        prev === "Change" ? "Hours" : "Change"
                      )
                    }
                  />
                </div>
                <div className="schedule-mobile-header">
                  <Button
                    icon="chevronprev"
                    stylingMode="text"
                    onClick={() => changeMonth("previous")}
                  />
                  <div className="schedule-mobile-title">
                    {`${_.upperFirst(translatedMonth)} ${format(
                      currentMonth,
                      "yyyy"
                    )}`}
                  </div>
                  <Button
                    icon="chevronnext"
                    stylingMode="text"
                    onClick={() => changeMonth("next")}
                  />
                  <Button
                    text={i18n.language === "en" ? "Today" : "Dziś"}
                    stylingMode="outlined"
                    onClick={() => setCurrentMonth(new Date())}
                  />
                </div>
                {(() => {
                  const selectedEntry = entries.find(
                    (e) =>
                      `${e.employee.companyId},${e.employee.id}` ===
                      selectedMobileEmployeeKey
                  );
                  if (!selectedEntry) return null;

                  const isBorrowedAndNotMine =
                    selectedEntry.employee.isBorrowed &&
                    !(
                      selectedEntry.employee.borrowedByManagerId ===
                        user?.employeeId &&
                      selectedEntry.employee.borrowedByManagerCompanyId ===
                        user?.companyId
                    );

                  return (
                    <div
                      className={clsx("schedule-mobile-list", {
                        "borrowed-disabled": isBorrowedAndNotMine,
                      })}
                    >
                      <div className="schedule-mobile-summary">
                        {t("schedule.planned", {
                          hours: selectedEntry.sum.sumaGodzin,
                          days: selectedEntry.sum.sumaDni,
                        })}
                      </div>
                      {eachDayOfInterval({
                        start: startOfMonth(currentMonth),
                        end: endOfMonth(currentMonth),
                      }).map((d) => {
                        const formattedDate = d.toLocaleDateString("en-CA");
                        const day = selectedEntry.days.find(
                          (x) =>
                            new Date(x.date).toLocaleDateString("en-CA") ===
                            formattedDate
                        );
                        const [hourStr] = (day?.hours.fromHour || "").split(
                          ":"
                        );
                        const hour = parseInt(hourStr, 10);
                        let changeLabel = "-";
                        if (hour >= 5 && hour <= 6) changeLabel = "I";
                        else if (hour >= 7 && hour <= 8)
                          changeLabel = "8.00";
                        else if (hour >= 13 && hour <= 14)
                          changeLabel = "II";
                        else if (hour >= 21 && hour <= 22)
                          changeLabel = "III";

                        const showHours =
                          !!day &&
                          day.hours.fromHour !== "00:00" &&
                          day.hours.toHour !== "00:00";

                        const todayStr = new Date().toLocaleDateString(
                          "en-CA"
                        );
                        const isToday = formattedDate === todayStr;
                        const dayShort = _.upperFirst(
                          format(d, "EEEEEE", {
                            locale: i18n.language === "en" ? enUS : pl,
                          })
                        );

                        const dt = getDayTypeFor(d);
                        return (
                          <div
                            key={formattedDate}
                            className={clsx("schedule-mobile-day", {
                              "schedule-mobile-day-free": dt === 2,
                              "schedule-mobile-day-holiday": dt === 3,
                              "schedule-mobile-day-today": isToday,
                            })}
                          >
                            <div className="schedule-mobile-day-left">
                              <div className="schedule-mobile-day-num">
                                {d.getDate()}
                              </div>
                              <div className="schedule-mobile-day-name">
                                {dayShort}
                              </div>
                            </div>
                            <div className="schedule-mobile-day-right">
                              {viewMode === "Change" && (
                                <div className="schedule-mobile-day-line">
                                  {changeLabel}
                                </div>
                              )}
                              {viewMode === "Hours" && showHours && (
                                <>
                                  <div className="schedule-mobile-day-line">{`${day!.hours.fromHour} - ${day!.hours.toHour}`}</div>
                                  {day!.hours.duration !== "" && (
                                    <div className="schedule-mobile-day-line-sub">
                                      {`(${day!.hours.duration})`}
                                    </div>
                                  )}
                                </>
                              )}
                              {viewMode === "Hours" && !showHours && (
                                <div className="schedule-mobile-day-line">
                                  -
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </>
            ) : (
              <>
              <div className="mb-4 flex flex-row justify-between">
                <div className="flex justify-center items-center gap-1">
                  <Button
                    icon="chevronprev"
                    stylingMode="text"
                    onClick={() => changeMonth("previous")}
                  />
                  <div className="w-32 text-center text-base">{`${translatedMonth} ${format(
                    currentMonth,
                    "yyyy"
                  )}`}</div>
                  <Button
                    icon="chevronnext"
                    stylingMode="text"
                    onClick={() => changeMonth("next")}
                  />
                </div>
                <Button
                  text={`${t("hoursType")}`}
                  stylingMode="contained"
                  onClick={() =>
                    setViewMode((prev) =>
                      prev === "Change" ? "Hours" : "Change"
                    )
                  }
                />
              </div>
              <div className="monthly">
              <div
                className={clsx(
                  "table-wrapper",
                  themeContext?.theme === "dark" ? "dark" : null
                )}
              >
                <table>
                  <thead>
                    <tr className="sticky">
                      <th className="sticky">
                        <div className="box-dark w-32 sm:w-60">
                          {t("schedule.employee")}
                        </div>
                      </th>
                      {entries[0]?.days?.map((day) => (
                        <th
                          key={day.date}
                          style={{
                            backgroundColor:
                              themeContext?.theme === "dark"
                                ? "#1e1e1e"
                                : "#f9f9f9",
                            zIndex: 2,
                          }}
                        >
                          <div className="box-dark" style={{ minWidth: 40 }}>
                            {_.upperFirst(
                              format(day.date, "EEEEEE, d.LL", {
                                locale: i18n.language === "en" ? enUS : pl,
                              })
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((entry) => {
                      const isBorrowedAndNotMine =
                        entry.employee.isBorrowed &&
                        !(
                          entry.employee.borrowedByManagerId ===
                            user?.employeeId &&
                          entry.employee.borrowedByManagerCompanyId ===
                            user?.companyId
                        );

                      return (
                        <tr
                          key={entry.employee.id}
                          className={clsx({
                            "borrowed-disabled": isBorrowedAndNotMine,
                          })}
                        >
                          <td className="sticky" style={{ height: "90px" }}>
                            <div className="box-dark w-32 sm:w-60 h-full">
                              <div className="font-bold text-wrap truncate">
                                {`${entry.employee.lastName} ${entry.employee.firstName}`}
                              </div>
                              <div className="text-wrap truncate">
                                {t("schedule.planned", {
                                  hours: entry.sum.sumaGodzin,
                                  days: entry.sum.sumaDni,
                                })}
                              </div>
                            </div>
                          </td>
                          {entry.days.map((day) => (
                            <td key={day.date} style={{ height: "90px" }}>
                              <div
                                className={clsx(
                                  "box-light",
                                  getClassForDay(day.date, themeContext?.theme)
                                )}
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  height: "100%",
                                  width: 100,
                                }}
                              >
                                <div>
                                  {viewMode === "Change" &&
                                    (() => {
                                      const [hourStr] =
                                        day.hours.fromHour.split(":");
                                      const hour = parseInt(hourStr, 10);

                                      if (hour >= 5 && hour <= 6) {
                                        return "I";
                                      } else if (hour >= 7 && hour <= 8) {
                                        return "8.00";
                                      } else if (hour >= 13 && hour <= 14) {
                                        return "II";
                                      } else if (hour >= 21 && hour <= 22) {
                                        return "III";
                                      } else {
                                        return "-";
                                      }
                                    })()}
                                  {viewMode === "Hours" &&
                                    day.hours.fromHour !== "00:00" &&
                                    day.hours.toHour !== "00:00" && (
                                      <>
                                        <div className="text-center">{`${day.hours.fromHour}-${day.hours.toHour}`}</div>
                                        {day.hours.duration !== "" && (
                                          <div className="text-center">{`(${day.hours.duration})`}</div>
                                        )}
                                      </>
                                    )}
                                </div>
                              </div>
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
              </>
            )}
            <Pagination
              itemCount={totalCount}
              pageIndex={pageIndex}
              pageSize={pageSize}
              showInfo={!isMobile}
              showPageSizeSelector={!isMobile}
              showNavigationButtons
              displayMode={isMobile ? "compact" : "full"}
              onPageIndexChange={handlePageClick}
              onPageSizeChange={setPageSize}
              allowedPageSizes={[10, 20, 40, 50, 60]}
            />
            <div className="flex flex-wrap justify-center gap-4 pt-2">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-[#a3e635]"></div>
                <div className="leading-none">{t("Holiday")}</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-[#4d7c0f]"></div>
                <div className="leading-none">{t("Free day")}</div>
              </div>
            </div>
          </>
        )}
      </div>
    </ScrollView>
  );
};
